import React, { useEffect, useRef } from 'react';
import './index.scss';
import { formatarPrecoReal } from '../../utils/Validadores';
import { formatarPrecoUnitario } from '../../utils/precoUnitario';

/**
 * Modal de detalhe de produto/variação usada nas telas de Cadastrar e Editar Pedido.
 *
 * Props:
 *  - produto   : objeto com dados do item (campos flexíveis, veja abaixo)
 *  - onClose   : callback para fechar a modal
 *  - nivel     : nível de acesso do cliente (ex: 'Atacadista') — opcional
 */
export default function ModalDetalheProduto({ produto, onClose, nivel }) {
    const dialogRef = useRef(null);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog || dialog.open) return;

        dialog.showModal();
    }, []);

    if (!produto) return null;

    // Normaliza campos — aceita tanto formato "lista de produtos" quanto "carrinho"
    const nome = produto.nome_produto || produto.nome || '';
    const imagem = produto.imagem || (produto.imagens && produto.imagens[0]) || null;
    const cor = produto.cor || '';
    const tamanho = produto.tamanho || '';
    const material = produto.material || '';
    const tipo = produto.tipo || '';
    const unidadesPorPacote = produto.unidades_por_pacote || 1;

    // Preço — carrinho já tem "preco", lista de produtos tem preco_varejo / preco_atacado
    const preco = produto.preco
        ?? (nivel === 'Atacadista' ? produto.preco_atacado : produto.preco_varejo)
        ?? produto.preco_varejo
        ?? 0;

    const precoAtacado = produto.preco_atacado ?? null;
    const precoVarejo = produto.preco_varejo ?? null;

    // Descrição da variação
    const descricaoVariacao = produto.nome && produto.nome_produto
        ? produto.nome  // campo "nome" do carrinho é a descrição da variação
        : [cor, tamanho, material, tipo].filter(Boolean).join(' / ');

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <dialog
            ref={dialogRef}
            className="modal-detalhe-produto-overlay"
            onClick={handleOverlayClick}
            onCancel={onClose}
        >
            <div className="modal-detalhe-produto">
              <div className="modal-detalhe-scroll">
                {/* Imagem no topo */}
                <div className="modal-detalhe-imagem">
                    {imagem ? (
                        <img src={imagem} alt={nome} />
                    ) : (
                        <div className="sem-imagem">Sem Imagem</div>
                    )}
                </div>

                {/* Conteúdo */}
                <div className="modal-detalhe-conteudo">
                    <h2 className="modal-detalhe-nome">{nome}</h2>

                    {descricaoVariacao && (
                        <p className="modal-detalhe-variacao">{descricaoVariacao}</p>
                    )}

                    <div className="modal-detalhe-info">
                        {cor && (
                            <div className="info-linha">
                                <strong>Cor:</strong> <span>{cor}</span>
                            </div>
                        )}
                        {tamanho && (
                            <div className="info-linha">
                                <strong>Tamanho:</strong> <span>{tamanho}</span>
                            </div>
                        )}
                        {material && (
                            <div className="info-linha">
                                <strong>Material:</strong> <span>{material}</span>
                            </div>
                        )}
                        {tipo && (
                            <div className="info-linha">
                                <strong>Tipo:</strong> <span>{tipo}</span>
                            </div>
                        )}
                        {unidadesPorPacote > 1 && (
                            <div className="info-linha">
                                <strong>Unidades por Pacote:</strong> <span>{unidadesPorPacote}</span>
                            </div>
                        )}
                    </div>

                    <div className="modal-detalhe-precos">
                        <div className="preco-principal">
                            <strong>Preço:</strong> <span>{formatarPrecoReal(preco)}</span>
                        </div>
                        {unidadesPorPacote > 1 && (
                            <div className="preco-unitario">
                                <strong>Preço/Unidade:</strong> <span>{formatarPrecoUnitario(preco, unidadesPorPacote)}</span>
                            </div>
                        )}
                        {precoVarejo !== null && precoAtacado !== null && (
                            <div className="precos-niveis">
                                <div className="preco-nivel">
                                    <strong>Varejo:</strong> <span>{formatarPrecoReal(precoVarejo)}</span>
                                </div>
                                <div className="preco-nivel">
                                    <strong>Atacado:</strong> <span>{formatarPrecoReal(precoAtacado)}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {produto.quantidade !== undefined && (
                        <div className="modal-detalhe-quantidade">
                            <strong>Quantidade no Carrinho:</strong> <span>{produto.quantidade}</span>
                            <span className="total-item"> | <strong>Total:</strong> {formatarPrecoReal(preco * produto.quantidade)}</span>
                        </div>
                    )}
                </div>
              </div>

                <button className="modal-detalhe-btn-ok" onClick={onClose}>
                    OK
                </button>
            </div>
        </dialog>
    );
}
